import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import educationDetailsBanner from "../../../images/education-details-banner.webp";
import useDebouncedSearch from "../../../components/filter/customHooks/useDebouncedSearch";
import LeadGenCtaBtn from "../../../features/lead-generation/LeadGenCtaBtn";

// Define interfaces for the nested board structure
interface BoardItem {
  id: number;
  boardName: string;
  boardTitle: string;
}

interface StateBoard {
  stateBoardName: string;
  boards: BoardItem[];
}

interface BoardCategory {
  boardLevel: string;
  stateBoards: StateBoard[];
}

const EducationBanner: React.FC<{ boards: BoardCategory[] }> = ({ boards = [] }) => {
  const navigate = useNavigate();
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  
  // Use the debounced search hook
  const { searchKeyword, handleKeywordChange, deboundedSearchKeyword } = useDebouncedSearch();
  const [filteredBoards, setFilteredBoards] = useState<BoardItem[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Extract all individual boards from the nested structure for searching
  const getAllBoards = (): BoardItem[] => {
    if (!Array.isArray(boards)) return [];
    
    const allBoards: BoardItem[] = [];
    
    boards.forEach(category => {
      if (category?.stateBoards) {
        category.stateBoards.forEach(stateBoard => {
          if (stateBoard?.boards) {
            stateBoard.boards.forEach(board => {
              if (board) allBoards.push(board);
            });
          }
        });
      }
    });
    
    return allBoards;
  };

  // Filter boards when search keyword changes
  useEffect(() => {
    if (deboundedSearchKeyword.trim() === '') {
      setFilteredBoards([]);
      setShowResults(false);
      return;
    }

    const allBoards = getAllBoards();
    
    const filtered = allBoards.filter(board => 
      board.boardName?.toLowerCase().includes(deboundedSearchKeyword.toLowerCase()) ||
      board.boardTitle?.toLowerCase().includes(deboundedSearchKeyword.toLowerCase())
    );
    
    setFilteredBoards(filtered);
    setShowResults(true);
  }, [deboundedSearchKeyword, boards]);

  // Handle clicking outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle board selection
  const handleBoardSelect = (board: BoardItem) => {
    if (board?.boardName) {
      navigate(`/education-board/${board.boardName}`);
      setShowResults(false);
      handleKeywordChange('');
    }
  };

  // Clear search input
  const handleClearSearch = () => {
    handleKeywordChange('');
    setShowResults(false);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setShowResults(false);
    } else if (e.key === 'Enter' && filteredBoards.length > 0) {
      handleBoardSelect(filteredBoards[0]);
    }
  };

  return (
    <div
      className="w-full text-white pt-10"
      style={{
        background:
          "linear-gradient(90.13deg, #0B3D8C 0.11%, #107CC4 54.49%, rgba(6, 175, 169, 0.745055) 83.43%)",
      }}
    >
      {/* <div className="container mx-auto flex flex-col gap-6 md:pr-0"> */}
        <div className="flex flex-col md:flex-row gap-5">
          <div className="flex flex-col w-full md:w-1/2 py-7 md:py-20 justify-content-center">
            <div className="max-w-full sm:max-w-[497px] mx-auto md:mx-10 my-auto">
              <h1 className="text-2xl md:text-4xl font-semibold leading-snug">
                Search for Boards in India
              </h1>
              
              <div ref={searchBoxRef} className="relative mt-6 max-w-lg text-xs font-normal">
                <input
                  type="text"
                  placeholder="Start Board, National Board and International Board"
                  className="w-full p-3 pl-10 text-black rounded-md shadow-md focus:outline-none"
                  value={searchKeyword}
                  onChange={(e) => handleKeywordChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  aria-label="Search education boards"
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="19"
                  height="19"
                  viewBox="0 0 19 19"
                  fill="none"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A3A2AC]"
                >
                  <path
                    d="M13.3484 12.1421C14.2435 10.878 14.7789 9.32527 14.7789 7.64201C14.7789 3.42835 11.4638 0 7.38942 0C3.31501 0 0 3.42835 0 7.64201C0 11.8557 3.31505 15.284 7.38945 15.284C9.01707 15.284 10.5186 14.7303 11.7409 13.8046L16.5819 18.811L18.1895 17.1485C18.1895 17.1485 13.3484 12.1421 13.3484 12.1421ZM7.38945 12.9326C4.56844 12.9326 2.27369 10.5594 2.27369 7.64201C2.27369 4.72459 4.56844 2.3514 7.38945 2.3514C10.2105 2.3514 12.5052 4.72459 12.5052 7.64201C12.5052 10.5594 10.2104 12.9326 7.38945 12.9326Z"
                    fill="#173CBA"
                  />
                </svg>
                
                {searchKeyword && (
                  <button 
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={handleClearSearch}
                  >
                    ×
                  </button>
                )}
                
                {/* Search Results Dropdown */}
                {showResults && filteredBoards.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <ul className="py-1">
                      {filteredBoards.map((board) => (
                        <li 
                          key={board.id} 
                          className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleBoardSelect(board)}
                        >
                          {board.boardTitle || board.boardName}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {showResults && filteredBoards.length === 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg p-4 text-sm text-gray-500">
                    No boards found matching your search.
                  </div>
                )}
              </div>

              {/* <div className="flex gap-4 mt-10">
                <LeadGenCtaBtn
                  pageName="education-board"
                  ctaName="board-alerts"
                  className="bg-green hover:bg-green text-white px-6 py-3 rounded font-semibold shadow flex items-center gap-2"
                >
                  Get Board Alerts
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="12"
                    viewBox="0 0 15 12"
                    fill="none"
                  >
                    <path
                      d="M1 5.25C0.585786 5.25 0.25 5.58579 0.25 6C0.25 6.41421 0.585786 6.75 1 6.75L1 5.25ZM14.5303 6.53033C14.8232 6.23744 14.8232 5.76256 14.5303 5.46967L9.75736 0.6967C9.46447 0.403806 8.98959 0.403806 8.6967 0.6967C8.40381 0.989593 8.40381 1.46447 8.6967 1.75736L12.9393 6L8.6967 10.2426C8.40381 10.5355 8.40381 11.0104 8.6967 11.3033C8.98959 11.5962 9.46447 11.5962 9.75736 11.3033L14.5303 6.53033ZM1 6.75L14 6.75L14 5.25L1 5.25L1 6.75Z"
                      fill="white"
                    />
                  </svg>
                </LeadGenCtaBtn>
              </div> */}
            </div>
          </div>
          
            <div className="w-full md:w-1/2 flex items-end mt-auto md:mt-0 ml-auto self-end">
              <img
                src="https://cdncollegementor.blob.core.windows.net/education/education-details-banner.webp"
                alt="education details banner"
                className="w-full"
              />
            </div>
        </div>
      {/* </div> */}
    </div>
  );
};

export default EducationBanner;